import * as bootstrap from 'bootstrap';
import * as Ladda from 'ladda';
import axios, { AxiosProgressEvent, AxiosResponse } from './axios';
import * as buttons from './buttons';
import { MaybeFunc } from './functions';
import { FromString } from './html';
import { GetIconLibrary } from './icons';
import * as models from './models';
import { Render } from './pdf';
import * as region from './region';
import { Clone } from './template';
import * as toast from './toast';
import { URLToBlob } from './util';
import { UUID } from './uuid';
import * as zip from './zip';

declare interface myWindow extends Window {
    readonly $: unknown;
    readonly Ladda: typeof Ladda;
    readonly cloudfront?: string;
    readonly cloudFront?: string;
}
declare const window: myWindow;

const toastSupport = typeof window.$ === 'object' && window.$ && 'toast' in window.$;
const laddaSupport = 'Ladda' in window && window.Ladda;

type hideShowFn = (e: HTMLElement[]) => void;
type filterHiddenFn = (e: HTMLElement[]) => HTMLElement[];
type isHiddenFn = (e: HTMLElement) => boolean;

export declare interface HandlerFuncs {
    onSelected?: (blobURL: string, name: string) => void;
    onUploadCompleted?: (uploadID: number) => void;
}

export interface UploadOptions {
    confirmBtn?: HTMLButtonElement;
    thumbnails?: 'after' | 'before' | HTMLElement | false;
    thumbnailHeight?: string | false;
    thumbnailMaxHeight?: string | false;
    thumbnailTemplate?: HTMLTemplateElement;
    success?: ($thumb: HTMLElement, uploadID: number, blobURL: string) => void;
    companyID?: string;
    company?: HTMLSelectElement;
    onchange?: () => void;
    onidschange?: () => void;
    tags?: () => string[];
    uploadIDs?: number[];
    cloudfront?: string;

    dropZone?: HTMLElement;
    dropZones?: HTMLElement[];

    hide?: hideShowFn;
    show?: hideShowFn;
    filterHidden?: filterHiddenFn;
    isHidden?: isHiddenFn;

    start?: () => void;
    stop?: () => void;

    disableToast?: boolean;

    handlers?: MaybeFunc<HandlerFuncs>;
}

declare interface createUploadsResp {
    files: file[];
}

declare interface file {
    uploadID: number;
    url: string;
    name: string;
    mime: string;
}

declare interface uploadResp extends models.Upload {
    hasRekognition?: boolean;
    labels: {
        label: string;
        confidence: number;
    }[];
    moderationLabels: {
        label: string;
        confidence: number;
    }[];
    combinedScore?: number;
}

declare interface uploadsResp {
    uploads: uploadResp[];
}

let regionCheck: Promise<string> | undefined;

function checkRegion() {
    if (!regionCheck) {
        regionCheck = region.Closest();
    }
    return regionCheck;
}

interface uploaderUpload {
    uploadID: number;
    url: string;
    blobURL: string;
    $thumb?: HTMLElement;
    hidden?: boolean;
}

export class Uploader {
    OnUpload?: (f: file, thumb: HTMLElement) => void;
    OnChange?: () => void;
    OnIDsChange?: () => void;

    // bootstrap defaults, may need changed for other frameworks like shopify
    private hide: hideShowFn = e => e.forEach(e => e.classList.add('d-none'));
    private show: hideShowFn = e => e.forEach(e => e.classList.remove('d-none'));

    private uploads: uploaderUpload[] = [];

    constructor(label: HTMLLabelElement, private opts: UploadOptions = {}) {
        opts.dropZone ??= label;
        opts.dropZones ??= [opts.dropZone];
        opts.start ??= () => {
            if (labelIsButton) {
                if (laddaSupport) {
                    l.start();
                } else {
                    buttons.Start(label);
                }
            }
        };
        opts.stop ??= () => {
            if (labelIsButton) {
                if (laddaSupport) {
                    l.stop();
                } else {
                    buttons.Stop(label);
                }
            }
        };

        if (opts.hide) {
            this.hide = opts.hide;
        }
        if (opts.show) {
            this.show = opts.show;
        }

        if (opts.onchange) {
            this.OnChange = opts.onchange;
        }

        if (opts.onidschange) {
            this.OnIDsChange = opts.onidschange;
        }

        const { thumbnails = 'after', success } = opts;

        let fileIndex = 0;
        const progress: Record<string, { loaded: number; total: number }> = {};

        const labelIsButton = label.classList.contains('btn');

        let l: Ladda.LaddaButton;
        let confirmButtonLadda: Ladda.LaddaButton | undefined;
        if (labelIsButton && laddaSupport) {
            l = Ladda.create(label as unknown as HTMLButtonElement);
            if (opts.confirmBtn) {
                confirmButtonLadda = Ladda.create(opts.confirmBtn);
            }
        }

        function drawThumb(uploadID: number, src: string, name: string, $img?: HTMLImageElement) {
            let thumb: HTMLElement;
            let img: HTMLImageElement | undefined;
            let close: HTMLElement | undefined;

            if (!opts.thumbnailTemplate) {
                thumb = document.createElement('div');
                thumb.classList.add('upload-thumbnail');

                close = document.createElement('a');
                close.classList.add('upload-thumbnail-close');
                thumb.appendChild(close);

                let icon: HTMLElement;
                switch (GetIconLibrary()) {
                case 'fontAwesome4':
                    icon = FromString('<i class="fa fa-times"></i>');
                    break;
                case 'fontAwesome5':
                    icon = FromString('<i class="fas fa-times"></i>');
                    break;
                case 'materialSymbolsRounded':
                    icon = FromString('<span class="material-symbols-rounded">close</span>');
                    break;
                }

                close.appendChild(icon);

                if ($img) {
                    img = $img;
                } else {
                    img = document.createElement('img');
                }
                if (opts.thumbnailMaxHeight !== false) {
                    img.style.maxHeight = opts.thumbnailMaxHeight || getComputedStyle(label).height || '1.5rem';

                    opts.thumbnailHeight ??= false;
                }
                thumb.appendChild(img);

                if (opts.thumbnailHeight !== false) {
                    thumb.style.height = opts.thumbnailHeight || getComputedStyle(label).height || '1.5rem';
                }
            } else {
                thumb = Clone(opts.thumbnailTemplate).firstElementChild as HTMLElement;

                img = thumb.querySelector('img') as HTMLImageElement;
                if (!img) {
                    throw new Error('Image element required.');
                }

                close = thumb.querySelector('.upload-thumbnail-close') as HTMLElement;
                if (!close) {
                    throw new Error('Close thumbnail element required.');
                }

                const fileName = thumb.querySelector<HTMLElement>('.file-name');
                if (fileName) {
                    fileName.innerText = name;
                }
            }

            thumb.setAttribute('data-upload-id', String(uploadID));
            void Load(img, src, name);

            if (success) {
                success(thumb, uploadID, src);
            } else {
                const space = document.createTextNode(' ');
                if (thumbnails === 'after') {
                    label.after(thumb);
                    label.after(space);
                } else if (thumbnails === 'before') {
                    label.before(thumb);
                    label.before(space);
                } else if (thumbnails) {
                    thumbnails.appendChild(thumb);
                    thumbnails.append(space);
                }
            }

            return { thumb, img, close };
        }

        const input = label.querySelector('input');
        // start checking for region
        input?.addEventListener('click', () => checkRegion());
        input?.addEventListener('change', async () => {
            this.OnChange?.();

            if (!input.files?.length) {
                return;
            }

            if (!opts.start) {
                throw new Error('opts.start is required');
            }
            opts.start();

            if (opts.confirmBtn) {
                if (confirmButtonLadda) {
                    confirmButtonLadda.start();
                } else {
                    buttons.Start(opts.confirmBtn);
                }
            }

            const handlers = new Array<HandlerFuncs>();
            if (opts.handlers) {
                handlers.length = input.files.length;
                for (let i = 0; i < input.files.length; i++) {
                    if (typeof opts.handlers === 'function') {
                        handlers[i] = opts.handlers();
                    } else if (opts.handlers) {
                        handlers[i] = opts.handlers;
                    }
                }
            }

            const files: { name: string; mime: string; tags?: string[] }[] = [];
            const inputFiles: File[] = [];
            for (let i = 0; i < input.files.length; i++) {
                const f = input.files[i];
                handlers[i]?.onSelected?.(URL.createObjectURL(f), f.name);

                files.push({ name: f.name, mime: f.type, tags: opts.tags?.() });
                inputFiles.push(f);
            }
            void axios.post<createUploadsResp>('/uploads', {
                'files': files,
                'companyID': opts.companyID || opts.company?.value,
                'region': await checkRegion(),
            }).then(resp => {
                for (let k = 0; k < resp.data.files.length; k++) {
                    const f = resp.data.files[k];
                    const i = fileIndex++;

                    const currUpload: uploaderUpload = {
                        uploadID: f.uploadID,
                        url: f.url,
                        blobURL: URL.createObjectURL(inputFiles[k]),
                    };
                    this.uploads.push(currUpload);

                    this.OnIDsChange?.();

                    const thumbnailElements = drawThumb(currUpload.uploadID, currUpload.blobURL, f.name);
                    const thumb = thumbnailElements.thumb;
                    currUpload.$thumb = thumb;

                    let completed = false;

                    const controller = new AbortController();

                    void axios.put(f.url, inputFiles[k], {
                        headers: {
                            'Content-Type': 'binary/octet-stream',
                        },
                        onUploadProgress: (e: AxiosProgressEvent) => {
                            progress[i] = { loaded: e.loaded, total: e.total || 0 };

                            let overallLoaded = 0;
                            let overallTotal = 0;
                            for (const k of Object.keys(progress)) {
                                const p = progress[k];

                                overallLoaded += p.loaded;
                                overallTotal += p.total;
                            }

                            if (laddaSupport) {
                                l.setProgress(overallLoaded / overallTotal);
                            }
                        },
                        signal: controller.signal,
                        withCredentials: false,
                    }).catch(() => {
                        thumb.parentElement?.removeChild(thumb);
                        if (!opts.stop) {
                            throw new Error('opts.stop is required');
                        }
                        opts.stop();
                    }).then(async () => {
                        delete progress[i];
                        if (!Object.keys(progress).length) {
                            if (!opts.stop) {
                                throw new Error('opts.stop is required');
                            }
                            opts.stop();
                            if (opts.confirmBtn) {
                                if (confirmButtonLadda) {
                                    confirmButtonLadda.stop();
                                } else {
                                    buttons.Stop(opts.confirmBtn);
                                }
                            }
                        }

                        handlers[k]?.onUploadCompleted?.(f.uploadID);

                        this.OnUpload?.(f, thumb);

                        completed = true;
                    });

                    thumbnailElements.close.addEventListener('click', e => {
                        e.preventDefault();

                        if (this.OnChange) {
                            this.OnChange();
                        }

                        if (!completed) {
                            thumb.parentElement?.removeChild(thumb);
                            this.uploads.splice(this.uploads.indexOf(currUpload), 1);
                            if (this.OnIDsChange) {
                                this.OnIDsChange();
                            }
                            controller.abort();
                            return;
                        }

                        this.hide([thumb]);
                        currUpload.hidden = true;
                        this.OnIDsChange?.();
                        const removeTimeout = setTimeout(() => {
                            thumb.parentElement?.removeChild(thumb);
                            this.uploads.splice(this.uploads.indexOf(currUpload), 1);
                        }, 15 * 1000);
                        const id = UUID();

                        let $toast: { reset: () => void } | undefined;
                        let t: bootstrap.Toast | undefined;
                        if (toastSupport && !opts.disableToast) {
                            $toast = $.toast({
                                text: `Removed attachment <a href="#" id="${id}">Undo</a>`,
                                hideAfter: 15 * 1000,
                            }) as unknown as typeof $toast;
                        } else if (!opts.disableToast) {
                            t = toast.Success(`Removed attachment <a href="#" id="${id}" class="text-reset text-decoration-underline">Undo</a>`);
                        }

                        const undo = document.getElementById(id);
                        undo?.addEventListener('click', e => {
                            e.preventDefault();

                            $toast?.reset();
                            t?.dispose();

                            clearTimeout(removeTimeout);
                            this.show([thumb]);
                            currUpload.hidden = false;
                            if (this.OnIDsChange) {
                                this.OnIDsChange();
                            }
                        });
                    });
                }
            });

            input.type = 'text';
            input.type = 'file';
            input.value = '';
        });

        for (const dropzone of opts.dropZones) {
            dropzone.addEventListener('drop', e => {
                e.stopPropagation();
                e.preventDefault();
                if (e.dataTransfer?.files && input) {
                    input.files = e.dataTransfer.files;
                    input.dispatchEvent(new Event('change'));
                }
            });

            dropzone.addEventListener('dragover', e => {
                e.stopPropagation();
                e.preventDefault();
            });
        }

        if (opts.uploadIDs?.length) {
            let cloudfront: string | undefined = opts.cloudfront;
            if (!opts.cloudfront) {
                cloudfront = window.cloudFront || window.cloudfront;
            }
            if (!cloudfront?.length) {
                throw new Error('missing cloudfront, please provide cloudfront in opts or set window.cloudfront');
            }
            void axios.get<uploadsResp>('/uploads', {
                params: {
                    'q': `id:${opts.uploadIDs.join(',')}`,
                },
            }).then(resp => {
                resp.data.uploads.forEach(u => {
                    const f: file = {
                        uploadID: u.id ?? 0,
                        url: `${cloudfront ?? ''}/${u.key ?? ''}`,
                        name: u.name || '',
                        mime: u.mime || '',
                    };

                    const currUpload: uploaderUpload = {
                        uploadID: f.uploadID,
                        url: f.url,
                        blobURL: '',
                    };
                    this.uploads.push(currUpload);

                    const img = document.createElement('img');
                    img.onload = async () => {
                        currUpload.blobURL = await fetch(f.url).
                            then(r => r.blob()).
                            then(b => URL.createObjectURL(b));
                    };

                    const thumbnailElements = drawThumb(f.uploadID, f.url, f.name);
                    const thumb = thumbnailElements.thumb;

                    thumbnailElements.close.addEventListener('click', e => {
                        e.preventDefault();

                        if (this.OnChange) {
                            this.OnChange();
                        }

                        this.hide([thumb]);
                        currUpload.hidden = true;
                        if (this.OnIDsChange) {
                            this.OnIDsChange();
                        }
                        const removeTimeout = setTimeout(() => {
                            thumb.parentElement?.removeChild(thumb);
                            this.uploads.splice(this.uploads.indexOf(currUpload), 1);
                        }, 15 * 1000);
                        const id = UUID();

                        let $toast: { reset: () => void } | undefined;
                        let t: bootstrap.Toast | undefined;
                        if (toastSupport && !opts.disableToast) {
                            $toast = $.toast({
                                text: `Removed attachment <a href="#" id="${id}">Undo</a>`,
                                hideAfter: 15 * 1000,
                            }) as unknown as typeof $toast;
                        } else if (!opts.disableToast) {
                            t = toast.Success(`Removed attachment <a href="#" id="${id}" class="text-white text-decoration-underline">Undo</a>`);
                        }

                        const undo = document.getElementById(id);
                        undo?.addEventListener('click', e => {
                            e.preventDefault();

                            $toast?.reset();
                            t?.dispose();

                            clearTimeout(removeTimeout);
                            this.show([thumb]);
                            currUpload.hidden = false;
                            if (this.OnIDsChange) {
                                this.OnIDsChange();
                            }
                        });
                    });
                });
            });
        }
    }

    GetUploadIDs(): number[] {
        const uploadIDs: number[] = [];

        for (const u of this.uploads) {
            if (u.hidden) {
                continue;
            }

            uploadIDs.push(u.uploadID);
        }

        return uploadIDs;
    }

    GetURLs(): string[] {
        const urls: string[] = [];

        for (const u of this.uploads) {
            if (u.hidden) {
                continue;
            }

            urls.push(u.url);
        }

        return urls;
    }

    GetBlobURLs(): string[] {
        const blobURLs: string[] = [];

        for (const u of this.uploads) {
            if (u.hidden) {
                continue;
            }

            blobURLs.push(u.blobURL);
        }

        return blobURLs;
    }

    HideUploads() {
        const $thumbs: HTMLElement[] = [];
        for (const u of this.uploads) {
            if (!u.$thumb) {
                continue;
            }
            $thumbs.push(u.$thumb);
        }
        this.hide($thumbs);
    }

    ShowUploads() {
        const $thumbs: HTMLElement[] = [];
        for (const u of this.uploads) {
            if (!u.$thumb) {
                continue;
            }
            $thumbs.push(u.$thumb);
        }
        this.show($thumbs);
    }

    RemoveHidden() {
        let changes = false;

        let i = 0;
        for (const u of this.uploads) {
            if (!u.hidden) {
                continue;
            }

            u.$thumb?.parentElement?.removeChild(u.$thumb);
            this.uploads[i++] = u;

            changes = true;
        }
        this.uploads.length = i;

        if (changes) {
            this.OnIDsChange?.();
        }
    }

    Clear() {
        if (!this.uploads.length) {
            return;
        }

        for (const u of this.uploads) {
            u.$thumb?.parentElement?.removeChild(u.$thumb);
        }
        this.uploads.length = 0;

        this.OnIDsChange?.();
    }

    Remove(uploadID: number) {
        let changes = false;

        for (const u of this.uploads) {
            if (u.uploadID === uploadID) {
                u.$thumb?.parentElement?.removeChild(u.$thumb);
                this.uploads.splice(this.uploads.indexOf(u), 1);
                changes = true;
                break;
            }
        }

        if (changes) {
            this.OnIDsChange?.();
        }
    }
}

const maxLoadableSize = 1 << 24;

export async function Load($img: HTMLImageElement, src: string, alt: string, size: number = 0): Promise<boolean> {
    $img.style.opacity = '0';

    const fail = () => {
        const div = document.createElement('div');
        div.classList.add('upload-thumbnail-fallback', 'text-truncate');
        div.textContent = alt;
        $img.parentElement?.replaceChild(div, $img);
    };

    if (size > maxLoadableSize) {
        fail();
        return false;
    }

    const load = async () => {
        const loaded = new Promise<true>(res => {
            $img.addEventListener('load', () => {
                $img.style.opacity = '';
                res(true);
            });
        });

        const error = new Promise<false>(res => {
            $img.addEventListener('error', () => {
                res(false);
            });
        });

        $img.setAttribute('src', src);
        $img.setAttribute('alt', alt);
        $img.setAttribute('title', alt);
        $img.setAttribute('crossorigin', 'crossorigin');

        return Promise.race([loaded, error]);
    };

    if (await load()) {
        return true;
    }

    try {
        // maybe pdf?
        src = await Render(src);

        if (await load()) {
            // fix parent fancybox?
            setTimeout(() => {
                const fb = $img.closest('a[data-fancybox]');
                fb?.setAttribute('data-src', src);
            });

            return true;
        }
    } catch (err) {
        console.error(err);
    }

    fail();
    return false;
}

export function LoadAll($uploads: HTMLElement[], $download?: HTMLButtonElement, zipName?: string) {
    const uuid = UUID();

    const urls: { name: string; url: string }[] = [];
    $uploads.forEach(u => {
        let a: HTMLElement | null = u;
        if (!(a instanceof HTMLAnchorElement)) {
            a = u.querySelector('a');
        }

        if (!a) {
            throw new Error('no anchor');
        }

        const src = a.getAttribute('href');
        const name = a.getAttribute('data-name');
        const size = parseInt(a.getAttribute('data-size') ?? '0', 10);

        if (!name) {
            throw new Error('no name');
        }
        if (!src) {
            throw new Error('no src');
        }

        void (async () => {
            const img = u.querySelector('img');
            if (!img) {
                throw new Error('no img');
            }
            if (await Load(img, src, name, size)) {
                a.setAttribute('data-fancybox', uuid);
            }
        })();

        urls.push({ name, url: src });
    });

    if ($download && zipName) {
        if (!zipName.endsWith('.zip')) {
            zipName += '.zip';
        }

        let l: Ladda.LaddaButton;
        if (laddaSupport) {
            l = Ladda.create($download);
        }
        $download.setAttribute('href', '#');
        $download.addEventListener('click', e => {
            e.preventDefault();
            if (laddaSupport) {
                l.start();
            } else {
                buttons.Start($download);
            }

            if (!zipName) {
                throw new Error('no zip name');
            }
            void zip.DownloadURLs(zipName, urls).finally(() => {
                if (laddaSupport) {
                    l.stop();
                } else {
                    buttons.Stop($download);
                }
            });
        });
    }
}

export function CartItemOrderLineItems(cartItem: models.CartItem): models.OrderLineItem[] {
    const orderLineItem: models.OrderLineItem = {
        lineItemID: cartItem.uploadLineItem?.lineItemID,
        lineItemTypeID: cartItem.uploadLineItem?.lineItemTypeID,
        lineItemTypeSizeID: cartItem.uploadLineItem?.lineItemTypeSizeID,
        quantity: cartItem.quantity,
        price: cartItem.uploadLineItem?.price,
    };

    for (const childLineItem of cartItem.uploadLineItem?.childLineItems ?? []) {
        orderLineItem.orderChildLineItems ??= [];
        orderLineItem.orderChildLineItems.push({
            childLineItemID: childLineItem.childLineItemID,
            childLineItemOptionID: childLineItem.childLineItemOptionID,
            quantity: childLineItem.quantity,
            price: childLineItem.price,
        });
    }

    return [orderLineItem];
}

export interface NewFile {
    name: string;
    mime: string;
    data: Blob;
}

export async function New(files: NewFile[], companyID?: string): Promise<file[]>;
export async function New(name: string, mime: string, data: Blob, companyID?: string): Promise<file[]>;
export async function New(files: string | NewFile[], mime?: string, data?: Blob, companyID?: string): Promise<file[]> {
    if (typeof files === 'string') {
        files = [{ name: files, mime: mime ?? '', data: data ?? new Blob() }];
    }

    const resp = await axios.post<createUploadsResp>('/uploads', {
        'files': files.map(f => ({ name: f.name, mime: f.mime, tags: [] })),
        'region': await checkRegion(),
        'companyID': companyID,
    });

    const putPromises: Promise<AxiosResponse>[] = [];

    for (let i = 0; i < resp.data.files.length; i++) {
        putPromises.push(axios.put(resp.data.files[i].url, files[i].data, {
            headers: {
                'Content-Type': 'binary/octet-stream',
            },
            withCredentials: false,
        }));
    }

    await Promise.all(putPromises);

    return resp.data.files;
}

export interface NewFileFromURL {
    name: string;
    mime: string;
    url: string;
}

export async function NewFromURL(files: NewFileFromURL[]): Promise<file[]>;
export async function NewFromURL(name: string, mime: string, url: string): Promise<file[]>;
export async function NewFromURL(files: string | NewFileFromURL[], mime?: string, url?: string): Promise<file[]> {
    if (typeof files === 'string') {
        files = [{ name: files, mime: mime ?? '', url: url ?? '' }];
    }

    const blobFiles = await Promise.all(files.map(async f => {
        const blob = await URLToBlob(f.url);
        return { name: f.name, mime: f.mime, data: blob };
    }));

    return New(blobFiles);
}